import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImagePlus, Trash2, Play, X, Images, Loader2 } from 'lucide-react';
import { listEventMedia, uploadEventMedia, deleteEventMedia, eventMediaUrl, EventMedia } from '../api';
import { Skeleton } from '../../../components/Skeleton';
import EmptyState from '../../../components/EmptyState';
import { useToast } from '../../../context/ToastContext';

export default function EventGallery({ eventId, isAdmin }: { eventId: number; isAdmin: boolean }) {
  const toast = useToast();
  const [media, setMedia] = useState<EventMedia[] | null>(null);
  const [uploading, setUploading] = useState(0);
  const [lightbox, setLightbox] = useState<EventMedia | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => listEventMedia(eventId).then((r) => setMedia(r.data)).catch(() => setMedia([])), [eventId]);
  useEffect(() => { load(); }, [load]);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(files.length);
    let ok = 0;
    for (const f of files) {
      try { await uploadEventMedia(eventId, f); ok++; }
      catch (err: any) { toast.error(`${f.name}: ${err?.message || 'no se pudo subir'}`); }
      setUploading((n) => n - 1);
    }
    if (ok) toast.success(`${ok} archivo(s) subido(s)`);
    if (inputRef.current) inputRef.current.value = '';
    setUploading(0);
    load();
  }

  async function borrar(m: EventMedia) {
    if (!confirm('¿Borrar este archivo de la galería?')) return;
    try { await deleteEventMedia(eventId, m.media_id); toast.success('Borrado'); setLightbox(null); load(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo borrar'); }
  }

  if (!media) return <Skeleton className="h-48 w-full rounded-2xl" />;

  return (
    <div>
      {/* Cabecera con el lenguaje visual de las fichas (Carlos 15-ago): la línea la lleva el
          renglón entero porque el título comparte fila con el botón de subir. */}
      <div className="mb-3 flex items-center justify-between gap-2 border-b border-line pb-1.5">
        <h3 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-content"><Images size={13} className="text-accent-ink" /> Galería ({media.length})</h3>
        {isAdmin && (
          <>
            <input ref={inputRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={onPick} />
            <button onClick={() => inputRef.current?.click()} disabled={uploading > 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-on-accent hover:opacity-90 disabled:opacity-60">
              {uploading > 0 ? <><Loader2 size={15} className="animate-spin" /> Subiendo… ({uploading})</> : <><ImagePlus size={15} /> Subir fotos/videos</>}
            </button>
          </>
        )}
      </div>

      {media.length === 0 ? (
        <EmptyState icon={Images} title="Galería vacía" subtitle={isAdmin ? 'Sube fotos o videos del evento.' : 'Aún no hay material del evento.'} compact />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {media.map((m) => (
            <button key={m.media_id} onClick={() => setLightbox(m)}
              className="group relative aspect-square overflow-hidden rounded-xl border border-line bg-surface-2">
              {m.kind === 'photo' ? (
                <img src={eventMediaUrl(eventId, m.media_id)} alt={m.caption || ''} loading="lazy"
                  className="h-full w-full object-cover transition group-hover:scale-105" />
              ) : (
                <>
                  <video src={eventMediaUrl(eventId, m.media_id)} className="h-full w-full object-cover" preload="metadata" muted />
                  <div className="absolute inset-0 grid place-items-center bg-black/30">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-white/90 text-content"><Play size={18} className="ml-0.5" /></div>
                  </div>
                </>
              )}
              {isAdmin && (
                <span onClick={(e) => { e.stopPropagation(); borrar(m); }}
                  className="hover-reveal absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full bg-black/50 text-white opacity-0 transition hover:bg-red-500 group-hover:opacity-100" title="Borrar">
                  <Trash2 size={13} />
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {lightbox && createPortal(
        <div className="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)}>
          <button className="absolute right-4 top-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)} aria-label="Cerrar"><X size={26} /></button>
          <div className="modal-panel max-h-[90vh] max-w-4xl" onClick={(e) => e.stopPropagation()}>
            {lightbox.kind === 'photo'
              ? <img src={eventMediaUrl(eventId, lightbox.media_id)} alt="" className="max-h-[85vh] w-auto rounded-lg object-contain" />
              : <video src={eventMediaUrl(eventId, lightbox.media_id)} controls autoPlay className="max-h-[85vh] w-auto rounded-lg" />}
            <div className="mt-2 flex items-center justify-between text-sm text-white/80">
              <span>{lightbox.caption || lightbox.original_name}</span>
              {isAdmin && <button onClick={() => borrar(lightbox)} className="inline-flex items-center gap-1 text-white/70 hover:text-red-400"><Trash2 size={14} /> Borrar</button>}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
